import { useState } from 'react';
import { EmptyState } from '../components/States';
import { useTranslation } from 'react-i18next';
import type { Count, Variant } from '@teo/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Badge, Panel } from '../components/Panel';
import { Modal } from '../components/Overlay';
import { InventoryImportModal } from '../ops/InventoryImportModal';
import { Differences } from '../ops/Differences';
import { variantLabel } from '../components/Operation';
import { useLoad, useSubmit } from '../lib/useAsync';
import { useToast } from '../lib/useToast';
import { releaseKey, requestKey } from '../lib/idempotency';
import { formatDate, formatNumber } from '../lib/format';
import type { Language } from '../i18n';

interface CountLine {
  variant: Variant;
  physical: string;
}

const diffClass = (d: number) => (d > 0 ? 'auditpos' : d < 0 ? 'auditneg' : 'auditzero');

/** Auditoría: el conteo físico se guarda sin mover existencias; Gerencia lo confirma con motivo y TEO ajusta físico − sistema (R5). */
export function Audit() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage as Language;
  const { user, can } = useAuth();
  const { show, showError } = useToast();
  const { busy, run } = useSubmit();
  const places = user?.locations ?? [];
  const [selected, setSelected] = useState<number | null>(null);
  const locationId = selected ?? places[0]?.id ?? null;
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<Variant[] | null>(null);
  const [lines, setLines] = useState<CountLine[]>([]);
  const [note, setNote] = useState('');
  const [open, setOpen] = useState<Count | null>(null);
  const [reason, setReason] = useState('');
  const [importing, setImporting] = useState(false);

  // El saldo del sistema se consulta por variante al agregarla o verla; una ubicación puede tener decenas de miles.
  const [system, setSystem] = useState<Map<number, number>>(new Map());
  const loadSystem = (ids: number[]) => {
    if (!locationId || ids.length === 0) return;
    api.inventory({ location_id: locationId, variant_ids: ids.slice(0, 500).join(','), limit: 500 })
      .then((rows) => setSystem((m) => { const n = new Map(m); for (const id of ids) n.set(id, 0); for (const r of rows) n.set(r.variant_id, r.qty); return n; }))
      .catch(showError);
  };
  const counts = useLoad(() => api.counts(), []);
  const scope = `conteo:${locationId}`;

  const search = (value: string) => {
    setTerm(value);
    if (value.trim().length < 2) return setResults(null);
    api.variants(value.trim()).then((found) => { setResults(found); loadSystem(found.slice(0, 30).map((v) => v.id)); }).catch(showError);
  };
  const add = (v: Variant) => {
    if (!lines.some((l) => l.variant.id === v.id)) setLines([...lines, { variant: v, physical: '' }]);
    setResults(null);
    setTerm('');
  };

  const save = () =>
    run(async () => {
      if (!locationId) return;
      const ready = lines.filter((l) => l.physical.trim() !== '');
      if (ready.length === 0) return show(t('audit.nothing'));
      try {
        const r = await api.createCount(requestKey(scope), { location_id: locationId, lines: ready.map((l) => ({ variant_id: l.variant.id, physical_qty: Number(l.physical) })), note });
        releaseKey(scope);
        setLines([]);
        setNote('');
        show(t('audit.saved', { doc: r.doc_no }));
        void counts.reload();
      } catch (e) {
        showError(e);
      }
    });

  const commit = () =>
    run(async () => {
      if (!open) return;
      const key = `conteo:confirmar:${open.id}`;
      try {
        const r = await api.commitCount(requestKey(key), open.id, reason);
        releaseKey(key);
        setOpen(null);
        show(t('audit.confirmed', { n: r.adjusted_lines ?? 0 }));
        void counts.reload();
        setSystem(new Map());
      } catch (e) {
        showError(e);
      }
    });

  const last = counts.data?.find((c) => c.status === 'CONFIRMED');
  const lastSystem = last?.lines.reduce((s, l) => s + (l.system_qty ?? 0), 0) ?? 0;
  const lastPhysical = last?.lines.reduce((s, l) => s + l.physical_qty, 0) ?? 0;
  const openSystem = (variantId: number, stored: number | null) => stored ?? (open?.location_id === locationId ? system.get(variantId) ?? 0 : null);

  return (
    <>
      <div className="kpis">
        <div className="kpi">
          <div className="lab">{t('audit.kpiSystem')}</div>
          <div className="val" id="aSystem">{formatNumber(lastSystem, lang)}</div>
          <div className="sub">{t('audit.kpiSystemSub')}</div>
        </div>
        <div className="kpi">
          <div className="lab">{t('audit.kpiPhysical')}</div>
          <div className="val" id="aPhysical">{formatNumber(lastPhysical, lang)}</div>
          <div className="sub">{t('audit.kpiPhysicalSub')}</div>
        </div>
        <div className="kpi">
          <div className="lab">{t('audit.kpiDifference')}</div>
          <div className="val" id="aDifference">{formatNumber(lastPhysical - lastSystem, lang)}</div>
          <div className="sub">{t('audit.kpiDifferenceSub')}</div>
        </div>
      </div>
      <div className="g2">
        <Panel title={t('audit.newTitle')} description={t('audit.newDescription')}>
          <div className="formgrid" id="countForm">
            {places.length > 1 ? (
              <div className="field span2">
                <label htmlFor="countLocation">{t('audit.location')}</label>
                <select id="countLocation" value={locationId ?? ''} onChange={(e) => { setSelected(Number(e.target.value)); setLines([]); setSystem(new Map()); }}>
                  {places.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            <div className="field span2">
              <label htmlFor="countSearch">{t('entry.search')}</label>
              <input id="countSearch" autoComplete="off" placeholder={t('entry.searchPlaceholder')} value={term} onChange={(e) => search(e.target.value)} />
              <div className="results" id="countResults">
                {results?.slice(0, 30).map((v) => (
                  <div className="result" key={v.id} data-variant={v.id}>
                    <div>
                      <b>{t('ops.refName', { ref: v.ref, name: v.product_name })}</b>
                      <small>{[variantLabel(v), t('audit.systemQty', { n: system.get(v.id) ?? 0 })].filter(Boolean).join(' · ')}</small>
                    </div>
                    <button type="button" onClick={() => add(v)}>
                      {t('common.add')}
                    </button>
                  </div>
                ))}
                {results && results.length === 0 ? <div className="empty">{t('ops.notFound')}</div> : null}
              </div>
            </div>
            <div className="span2 tablewrap">
              <table>
                <thead>
                  <tr>
                    <th>{t('audit.cols.product')}</th>
                    <th>{t('audit.cols.system')}</th>
                    <th>{t('audit.cols.physical')}</th>
                    <th>{t('audit.cols.difference')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody id="countLines">
                  {lines.map((l) => {
                    const sys = system.get(l.variant.id) ?? 0;
                    const d = l.physical.trim() === '' ? null : Number(l.physical) - sys;
                    return (
                      <tr key={l.variant.id} data-variant={l.variant.id}>
                        <td>
                          <b>{l.variant.ref}</b> {l.variant.product_name}
                          <br />
                          <span className="tiny muted">{variantLabel(l.variant)}</span>
                        </td>
                        <td>{formatNumber(sys, lang)}</td>
                        <td>
                          <input className="input" style={{ width: 90 }} type="number" min={0} data-field="physical" value={l.physical} onChange={(e) => setLines(lines.map((x) => (x === l ? { ...x, physical: e.target.value } : x)))} />
                        </td>
                        <td className={d == null ? '' : diffClass(d)}>{d == null ? '—' : d > 0 ? `+${d}` : d}</td>
                        <td>
                          <button type="button" className="btn sm" onClick={() => setLines(lines.filter((x) => x !== l))}>
                            {t('common.remove')}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {lines.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="empty">
                        {t('audit.linesEmpty')}
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            <div className="field span2">
              <label htmlFor="countNote">{t('audit.note')}</label>
              <input id="countNote" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <button type="button" className="btn black span2" id="countSave" disabled={busy || lines.length === 0} onClick={() => void save()}>
              {t('audit.save')}
            </button>
          </div>
        </Panel>

        <Panel
          title={t('audit.historyTitle')}
          description={t('audit.historyDescription')}
          badge={can('import.inventory') ? (
            <button type="button" className="btn" id="auditImportBtn" onClick={() => setImporting(true)}>
              {t('invImport.auditButton')}
            </button>
          ) : null}
        >
          <div id="auditHistory" className="alerts">
            {(counts.data ?? []).map((c) => (
              <div className="alert" key={c.id} data-count={c.doc_no}>
                <div>
                  <b>{[c.doc_no, c.location_name].join(' · ')}</b>
                  <small>{[formatDate(c.created_at, lang), c.created_by_name, t('audit.linesCount', { n: c.lines.length }), c.note].filter(Boolean).join(' · ')}</small>
                </div>
                <div className="actions">
                  <Badge tone={c.status === 'CONFIRMED' ? 'green' : c.status === 'DRAFT' ? 'amber' : 'red'}>{t(`docStatus.${c.status}`)}</Badge>
                  <button type="button" className="btn sm" data-open-count={c.doc_no} onClick={() => { setOpen(c); setReason(''); if (c.status === 'DRAFT' && c.location_id === locationId) loadSystem(c.lines.map((l) => l.variant_id)); }}>
                    {t(c.status === 'DRAFT' && can('inventory.adjust') ? 'audit.review' : 'common.view')}
                  </button>
                </div>
              </div>
            ))}
            {counts.data && counts.data.length === 0 ? <EmptyState icon="audit" title={t('audit.historyEmpty')} hint={t('audit.historyEmptyHint')} /> : null}
          </div>
        </Panel>
      </div>

      {can('audit.view') ? <div style={{ marginTop: 14 }}><Differences /></div> : null}

      <InventoryImportModal open={importing} defaultMode="audit" onClose={() => setImporting(false)} onImported={() => { void counts.reload(); setSystem(new Map()); }} />

      <Modal open={!!open} title={t('audit.modalTitle', { doc: open?.doc_no ?? '', location: open?.location_name ?? '' })} help={t('audit.modalHelp')} onClose={() => setOpen(null)}>
        {open ? (
          <div id="countReview">
            <div className="tablewrap">
              <table>
                <thead>
                  <tr>
                    <th>{t('audit.cols.product')}</th>
                    <th>{t('audit.cols.system')}</th>
                    <th>{t('audit.cols.physical')}</th>
                    <th>{t('audit.cols.difference')}</th>
                  </tr>
                </thead>
                <tbody>
                  {open.lines.map((l) => {
                    const sys = openSystem(l.variant_id, l.system_qty);
                    const d = sys == null ? null : l.physical_qty - sys;
                    return (
                      <tr key={l.variant_id}>
                        <td>
                          <b>{l.ref}</b> {l.product_name}
                          <br />
                          <span className="tiny muted">{variantLabel(l)}</span>
                        </td>
                        <td>{sys == null ? '—' : formatNumber(sys, lang)}</td>
                        <td>{formatNumber(l.physical_qty, lang)}</td>
                        <td className={d == null ? '' : diffClass(d)}>{d == null ? '—' : d > 0 ? `+${d}` : d}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {open.status === 'CONFIRMED' && open.reason ? <p className="help">{t('audit.reasonWas', { reason: open.reason })}</p> : null}
            {open.status === 'DRAFT' && can('inventory.adjust') ? (
              <div className="formgrid" style={{ marginTop: 10 }}>
                <div className="field span2">
                  <label htmlFor="countReason">{t('audit.reason')}</label>
                  <input id="countReason" value={reason} onChange={(e) => setReason(e.target.value)} />
                </div>
                <button type="button" className="btn black span2" id="countConfirm" disabled={busy || !reason.trim()} onClick={() => void commit()}>
                  {t('audit.confirm')}
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </>
  );
}
